import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { API } from '../lib/constants';
import {
  ArrowRight, Zap, Trophy, Play, Share2, Check, X, Film, GraduationCap, MapPin, BadgeCheck
} from 'lucide-react';

// Wall of Fame - NFL players who started on HUDL
const WALL_OF_FAME = [
  { name: 'Josh Allen', username: 'JoshAllen', school: 'Firebaugh HS', position: 'QB', team: 'Buffalo Bills', photo: 'https://static.hudl.com/users/prod/3242199_830e9c99264d49fbbef23de5b9ad7569.jpg' },
  { name: 'Jalen Hurts', username: 'JalenHurts', school: 'Channelview HS', position: 'QB', team: 'Philadelphia Eagles', photo: 'https://static.hudl.com/users/prod/2166961_0f27bc8a253147d09b86d4f797d14ba4.jpg' },
  { name: 'Ja\'Marr Chase', username: 'JaMarrChase', school: 'Archbishop Rummel', position: 'WR', team: 'Cincinnati Bengals', photo: 'https://static.hudl.com/users/prod/5293648_702417df352e452bb2b31fd924dcbe15.jpg' },
  { name: 'Myles Garrett', username: 'MylesGarrett', school: 'Martin HS', position: 'DE', team: 'Cleveland Browns', photo: 'https://static.hudl.com/users/prod/617331_dfe0596c8f66420284073cfea0673e97.jpg' },
  { name: 'Saquon Barkley', username: 'SaquonBarkley', school: 'Whitehall HS', position: 'RB', team: 'Philadelphia Eagles', photo: 'https://static.hudl.com/users/prod/1440965_5bb274c21b75402e86edcfa659a884d1.jpg' },
  { name: 'Lamar Jackson', username: 'LamarJackson', school: 'Boynton Beach HS', position: 'QB', team: 'Baltimore Ravens', photo: 'https://static.hudl.com/users/prod/2635357_33c15f0b5c9b40c682fa9ea63e8ff7eb.jpg' },
  { name: 'Justin Fields', username: 'JustinFields', school: 'Harrison HS', position: 'QB', team: 'Pittsburgh Steelers', photo: 'https://static.hudl.com/users/prod/4530230_4139b163b1b049fcb925cdbf8b87249c.jpg' },
  { name: 'Sam Darnold', username: 'SamDarnold', school: 'San Clemente HS', position: 'QB', team: 'Minnesota Vikings', photo: 'https://static.hudl.com/users/prod/1519280_4ccad20f81c34b7a9706bbd0a72f534d.jpg' },
];

const GREEN = '#0F5132';

const LandingPage = () => {
  const navigate = useNavigate();
  const [claimUsername, setClaimUsername] = useState('');
  const [legends, setLegends] = useState(WALL_OF_FAME);
  const isLoggedIn = localStorage.getItem('userId');

  React.useEffect(() => {
    axios.get(`${API}/api/wall-of-fame`)
      .then(r => { if (r.data?.legends?.length) setLegends(r.data.legends); })
      .catch(() => {});
  }, []);

  const handleClaim = () => {
    if (claimUsername.trim()) navigate(`/create-profile?username=${encodeURIComponent(claimUsername.trim())}`);
    else navigate('/create-profile');
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* ═══════ NAV ═══════ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/60">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')} data-testid="nav-logo">
            <img src="/offersz-o.png" alt="Offersz" className="w-9 h-9 rounded-full" />
            <span className="text-xl font-extrabold tracking-tight text-white">Offersz</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <Button onClick={() => navigate('/directory')} variant="ghost" className="text-zinc-300 hover:text-white hover:bg-white/5 rounded-full hidden sm:flex" data-testid="nav-discover">Discover</Button>

            {isLoggedIn ? (
              <Button onClick={() => navigate('/dashboard')} className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-semibold rounded-full px-6" data-testid="nav-dashboard">Dashboard</Button>
            ) : (
              <>
                <Button onClick={() => navigate('/signin')} variant="ghost" className="text-zinc-300 hover:text-white hover:bg-white/5 rounded-full" data-testid="nav-login">Log in</Button>
                <Button onClick={() => navigate('/create-profile')} className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-semibold rounded-full px-5" data-testid="nav-signup">Sign up free</Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ═══════ HERO — Dark pitch ═══════ */}
      <section className="relative bg-[#0A0D0A] pt-28 pb-16 lg:pb-24 px-4 overflow-hidden">
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-[radial-gradient(ellipse_at_bottom,_rgba(22,163,74,0.18),_transparent_60%)]" />
        <div className="max-w-6xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Left */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 text-xs font-medium text-zinc-300 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#16A34A]" />
                Built to help you say <span className="text-[#4ade80] font-semibold">"blessed to receive an offer from"</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white mb-6">
                Your Next Offer<br />
                <span className="text-[#16A34A]">Starts Here.</span>
              </h1>
              <p className="text-base sm:text-lg text-zinc-400 mb-8 max-w-md leading-relaxed">
                The recruiting profile for football players. Everything a coach needs to know. Your link.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 max-w-md">
                <div className="flex-1 min-w-0 flex items-center bg-white rounded-full shadow-lg overflow-hidden">
                  <span className="text-slate-400 text-sm pl-4 whitespace-nowrap">offersz.co/</span>
                  <input value={claimUsername} onChange={e => setClaimUsername(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))}
                    onKeyPress={e => e.key === 'Enter' && handleClaim()} placeholder="yourname" data-testid="hero-claim-input"
                    className="flex-1 min-w-0 py-4 pr-4 text-slate-900 font-semibold text-sm outline-none bg-transparent" />
                </div>
                <Button onClick={handleClaim} data-testid="hero-claim-btn" className="w-full sm:w-auto bg-[#0F5132] hover:bg-[#0B3B24] text-white font-semibold rounded-full px-6 py-4 h-auto text-sm whitespace-nowrap">Claim your link</Button>
              </div>
              <p className="text-zinc-500 text-xs mt-3 ml-1">Free forever. Takes 60 seconds. No typing — just drop your HUDL.</p>
            </div>

            {/* Right — Old Way vs New Way */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-stretch" data-testid="old-vs-new">
              {/* OLD WAY */}
              <div className="relative" data-testid="old-way-card">
                <div className="absolute -top-2.5 left-4 z-10 bg-red-100 text-red-700 border border-red-200 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full">Old Way</div>
                <div className="bg-zinc-900/80 border border-zinc-800 rounded-3xl p-3.5 h-full">
                  <div className="bg-white rounded-2xl overflow-hidden shadow">
                    <div className="h-10 bg-sky-500" />
                    <div className="px-3 pb-3 -mt-5">
                      <div className="w-11 h-11 rounded-full bg-orange-100 border-4 border-white flex items-center justify-center text-base">🏈</div>
                      <div className="mt-1">
                        <p className="font-bold text-[13px] text-slate-900 flex items-center gap-1">Jordan Smith <BadgeCheck className="h-3 w-3 text-sky-500" /></p>
                        <p className="text-slate-400 text-[11px]">@jsmith_qb7</p>
                        <p className="text-[10.5px] text-slate-700 mt-1.5 leading-snug break-words">
                          🏈 Central Valley '27 | QB/ATH | 6'1.5" 195 | 4.4 GPA | 2X Champ | 3X All-Conf | 📍 Midwest | 40: 4.52 | HUDL⬇️ | DMs Open 🙏
                        </p>
                        <div className="mt-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
                          <p className="text-[10px] text-slate-600">✉️ jsmith.recruit@gmail.com</p>
                          <p className="text-[10px] text-slate-600">📱 (317) 555-0847</p>
                        </div>
                        <div className="flex gap-3 mt-1.5 text-[10px] text-slate-400"><span><b className="text-slate-700">847</b> Following</span><span><b className="text-slate-700">1.2K</b> Followers</span></div>
                      </div>
                    </div>
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {['Cluttered & hard to read', 'No highlight videos', 'Limited character count', 'Contact info exposed publicly'].map((t, i) => (
                      <li key={i} className="flex items-center gap-1.5 text-[11px] text-zinc-400"><X className="h-3 w-3 text-red-500 flex-shrink-0" />{t}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* NEW WAY */}
              <div className="relative" data-testid="new-way-card">
                <div className="absolute -top-2.5 left-4 z-10 bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full">New Way</div>
                <div className="bg-[#16A34A]/10 border border-[#16A34A]/30 rounded-3xl p-3.5 h-full ring-1 ring-[#16A34A]/20">
                  {/* Same Twitter/X profile — one clean link replaces everything */}
                  <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
                    <div className="h-10 bg-gradient-to-r from-[#16A34A] to-emerald-400" />
                    <div className="px-3 pb-3 -mt-5">
                      <div className="w-11 h-11 rounded-full bg-emerald-50 border-4 border-white flex items-center justify-center text-base">🏈</div>
                      <div className="mt-1">
                        <p className="font-bold text-[13px] text-slate-900 flex items-center gap-1">Jordan Smith <BadgeCheck className="h-3 w-3 text-[#16A34A]" /></p>
                        <p className="text-slate-400 text-[11px]">@jsmith_qb7</p>
                        <p className="text-[10.5px] text-slate-700 mt-1.5 leading-snug">QB/ATH · Central Valley '27 🏈</p>
                        <a className="inline-flex items-center gap-1.5 mt-1.5 text-[11px] font-bold text-[#0F5132] bg-[#DCFCE7] border border-[#86EFAC] px-2.5 py-1 rounded-full">🔗 offersz.co/jordansmith</a>
                        <div className="flex gap-3 mt-1.5 text-[10px] text-slate-400"><span><b className="text-slate-700">847</b> Following</span><span><b className="text-slate-700">1.2K</b> Followers</span></div>
                      </div>
                    </div>
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {['One link replaces all that text', 'No exposed phone or email', 'Highlights, stats & offers inside', 'Clean, professional, recruiter-ready'].map((t, i) => (
                      <li key={i} className="flex items-center gap-1.5 text-[11px] text-zinc-200"><Check className="h-3 w-3 text-[#16A34A] flex-shrink-0" />{t}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ WALL OF FAME ═══════ */}
      <section className="bg-[#0A0D0A] py-20 px-4 overflow-hidden border-t border-zinc-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#16A34A] text-sm font-bold uppercase tracking-wider mb-2">Wall of Fame</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-3 tracking-tight">They all started with HUDL highlights.</h2>
            <p className="text-zinc-400 text-base sm:text-lg">Now they're in the NFL. Where will your link take you?</p>
          </div>
          <div className="flex overflow-x-auto gap-4 pb-4 scrollbar-hide -mx-4 px-4 snap-x snap-mandatory">
            {legends.map((player, i) => (
              <div key={i} className="flex-shrink-0 w-[220px] snap-start group cursor-pointer" onClick={() => navigate(`/profile/${player.username}`)} data-testid={`fame-${player.username}`}>
                <div className="bg-white/[0.03] border border-white/10 rounded-3xl overflow-hidden hover:border-[#16A34A]/40 transition-all duration-300">
                  <div className="aspect-[4/5] relative overflow-hidden">
                    <img src={player.photo || player.profile_picture_url} alt={player.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <span className="bg-[#16A34A] text-white text-[10px] font-extrabold px-2 py-1 rounded-full">{player.position || (player.positions || [])[0]}</span>
                      <h3 className="text-white font-extrabold text-lg mt-2">{player.name}</h3>
                      <p className="text-white/50 text-xs">{player.school}</p>
                      {(player.team || player.nfl_team) && <p className="text-[#4ade80] text-xs font-bold mt-1">{player.team || player.nfl_team}</p>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="flex-shrink-0 w-[220px] snap-start">
              <div className="bg-[#16A34A]/10 border border-[#16A34A]/25 rounded-3xl h-full flex flex-col items-center justify-center p-8 text-center">
                <div className="w-14 h-14 bg-[#16A34A] rounded-full flex items-center justify-center mb-4"><ArrowRight className="h-6 w-6 text-white" /></div>
                <h3 className="text-white font-extrabold text-lg mb-2">You're next.</h3>
                <p className="text-white/40 text-sm mb-4">Build your profile and start your journey.</p>
                <Button onClick={() => navigate('/create-profile')} className="bg-[#16A34A] hover:bg-[#0F5132] text-white font-semibold rounded-full text-sm">Get Started</Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ HOW IT WORKS ═══════ */}
      <section className="bg-white py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-3 tracking-tight">Create yours in 60 seconds.</h2>
            <p className="text-slate-500 text-lg">Three steps. One link. Every coach sees everything.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <Zap className="h-6 w-6" />, num: '01', title: 'Drop your HUDL link', desc: 'We auto-import your profile photo, stats, and highlight videos. No typing.' },
              { icon: <Trophy className="h-6 w-6" />, num: '02', title: 'Add your offers', desc: 'Showcase your college offers with school logos. Link your announcement tweets.' },
              { icon: <Share2 className="h-6 w-6" />, num: '03', title: 'Share everywhere', desc: 'Put offersz.co/yourname in your Twitter bio. Coaches will find you.' },
            ].map((step, i) => (
              <div key={i} className="text-center group">
                <div className="w-16 h-16 bg-[#DCFCE7] rounded-2xl flex items-center justify-center text-[#0F5132] mx-auto mb-5 group-hover:scale-110 transition-transform">{step.icon}</div>
                <p className="text-[#16A34A] text-xs font-extrabold tracking-wider mb-2">{step.num}</p>
                <h3 className="text-xl font-extrabold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FEATURES ═══════ */}
      <section className="bg-slate-50 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-3 tracking-tight">Everything coaches want to see.</h2>
            <p className="text-slate-500 text-lg">Built for high school football players chasing the next level.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: <Zap className="h-5 w-5" />, title: 'HUDL Auto-Import', desc: 'Drop your link. We pull highlights, stats, and your profile photo automatically.' },
              { icon: <Trophy className="h-5 w-5" />, title: 'Offer Tracker', desc: 'Display college offers front and center with logos and your announcement tweets.' },
              { icon: <Film className="h-5 w-5" />, title: 'Highlight Reels', desc: 'Your best plays, beautifully presented. Coaches watch them right on your page.' },
              { icon: <GraduationCap className="h-5 w-5" />, title: 'Stats & Measurables', desc: 'Height, weight, 40 time, GPA — everything coaches search for, one glance.' },
              { icon: <MapPin className="h-5 w-5" />, title: 'Mobile-First', desc: 'Designed for phones because that\'s where coaches check profiles.' },
              { icon: <Share2 className="h-5 w-5" />, title: 'Private Recruiter Inbox', desc: 'Coaches message you directly. A verified sender system keeps out the spam.' },
            ].map((feature, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:border-[#16A34A]/30 transition-all duration-300">
                <div className="w-10 h-10 rounded-xl bg-[#DCFCE7] text-[#0F5132] flex items-center justify-center mb-3">{feature.icon}</div>
                <h3 className="font-extrabold text-slate-900 mb-1">{feature.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FINAL CTA ═══════ */}
      <section className="bg-[#0A0D0A] py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-5xl font-extrabold text-white mb-4 leading-tight tracking-tight">Your next chapter<br />starts with one link.</h2>
          <p className="text-zinc-400 text-lg mb-8 max-w-xl mx-auto">Every day you wait is a day a coach doesn't see your highlights.</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 max-w-md mx-auto">
            <div className="flex-1 flex items-center bg-white rounded-full shadow-lg overflow-hidden">
              <span className="text-slate-400 text-sm pl-4 whitespace-nowrap">offersz.co/</span>
              <input value={claimUsername} onChange={e => setClaimUsername(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))}
                onKeyPress={e => e.key === 'Enter' && handleClaim()} placeholder="yourname" data-testid="footer-claim-input"
                className="flex-1 py-4 pr-4 text-slate-900 font-semibold text-sm outline-none bg-transparent" />
            </div>
            <Button onClick={handleClaim} data-testid="footer-claim-btn" className="w-full sm:w-auto bg-[#16A34A] hover:bg-[#0F5132] text-white font-semibold rounded-full px-6 py-4 h-auto text-sm whitespace-nowrap">Get started free</Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0D0A] border-t border-zinc-900 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/offersz-o.png" alt="Offersz" className="w-7 h-7 rounded-full" />
            <span className="text-sm font-bold text-white">Offersz</span>
          </div>
          <div className="flex gap-6">
            <button onClick={() => navigate('/directory')} className="text-white/40 text-sm hover:text-white/70">Discover</button>
            <button onClick={() => navigate('/create-profile')} className="text-white/40 text-sm hover:text-white/70">Create Profile</button>
            <button onClick={() => navigate('/signin')} className="text-white/40 text-sm hover:text-white/70">Sign In</button>
          </div>
          <p className="text-white/20 text-xs">The recruiting link for high school football players.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
